import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';
import { Trash2, Plus, Search, Send, BookOpen } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

export default function AdminCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '', type: 'danger' });
  
  const showConfirm = (title, message, action, type='danger') => setConfirmModal({ open: true, title, message, action, type });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '', type: 'danger' });

  const deleteCourse = (course) => {
    setConfirmModal({
      open: true,
      title: 'Eğitimi Sil',
      message: `"${course.title}" eğitimini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      type: 'danger',
      requireText: true,
      action: async () => {
        try {
          await api.delete('/courses/' + course.id);
          const r = await api.get('/courses');
          setCourses(r.data);
        } catch (e) {
          alert(e.response?.data?.error || 'Silinemedi');
        }
        closeConfirm();
      }
    });
  };

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Genel' });
  const [search, setSearch] = useState('');
  const [announcing, setAnnouncing] = useState(null);

  useEffect(() => {
    api.get('/courses').then(r => setCourses(r.data));
    api.get('/categories').then(r => setCategories(r.data));
  }, []);

  const createCourse = async () => {
    await api.post('/courses', form);
    const r = await api.get('/courses');
    setCourses(r.data); 
    setShowModal(false); 
    setForm({ title: '', description: '', category: 'Genel' });
  };

  const announce = (courseId, courseTitle) => {
    setConfirmModal({
      open: true,
      title: 'Eğitimi Duyur',
      message: `"${courseTitle}" eğitimi için tüm kullanıcılara mail gönderilecek. Emin misiniz?`,
      type: 'warning',
      requireText: false,
      action: async () => {
        setAnnouncing(courseId);
        try {
          const r = await api.post('/courses/' + courseId + '/announce');
          alert(r.data.sent + ' kullanıcıya mail gönderildi!');
        } catch(e) {
          alert('Hata oluştu');
        }
        setAnnouncing(null);
        closeConfirm();
      }
    });
  };

  const filtered = courses.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <AdminLayout>
      <div className="page-container">
        
        {/* Üst Başlık ve Aksiyon */}
        <div className="page-header">
          <div>
            <h2 className="page-title">Eğitimler</h2>
            <p className="page-subtitle">Platformdaki tüm eğitimleri buradan yönetebilir ve yeni eğitim ekleyebilirsiniz.</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={18} strokeWidth={2.5} /> 
            <span>Yeni Eğitim</span>
          </button>
        </div>

        {/* Ana İçerik Kartı */}
        <div className="content-card">
          
          {/* Arama Çubuğu */}
          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <Search size={18} className="search-icon" />
              <input 
                placeholder="Eğitim başlığına göre ara..." 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                className="search-input" 
              />
            </div>
            <div className="result-count">
              Toplam <strong>{filtered.length}</strong> eğitim
            </div>
          </div>

          {/* Tablo */}
          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  {['EĞİTİM ADI', 'KATEGORİ', 'DURUM', 'İŞLEM'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}>
                    <td className="font-medium text-dark">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="course-icon-bg">
                          <BookOpen size={16} />
                        </div>
                        {c.title}
                      </div>
                    </td>
                    <td>
                      <span className="badge category-badge">{c.category || 'Genel'}</span>
                    </td>
                    <td>
                      <span className={`badge ${c.is_active ? 'status-active' : 'status-passive'}`}>
                        {c.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => navigate('/yonetim/egitimler/' + c.id)} className="btn-outline">
                          Yönet
                        </button>
                        <button 
                          onClick={() => announce(c.id, c.title)} 
                          disabled={announcing === c.id}
                          className="btn-announce"
                        >
                          <Send size={14} /> {announcing === c.id ? 'Gönderiliyor...' : 'Duyur'}
                        </button>
                        {isAdmin && (
                          <button onClick={() => deleteCourse(c)} className="btn-icon-danger" title="Sil">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      Arama kriterlerinize uygun eğitim bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Yeni Eğitim Modalı */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Yeni Eğitim Oluştur</h3>
                <p className="modal-subtitle">Eğitimin temel bilgilerini girerek sisteme ekleyin.</p>
              </div>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">EĞİTİM BAŞLIĞI</label>
                <input 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                  className="form-input" 
                  placeholder="Örn: İleri Düzey Excel ve Veri Analizi" 
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">KATEGORİ</label>
                <select 
                  value={form.category} 
                  onChange={e => setForm({...form, category: e.target.value})}
                  className="form-input"
                >
                  {categories.length > 0 ? categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  )) : <option value="Genel">Genel</option>}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">AÇIKLAMA</label>
                <textarea 
                  value={form.description} 
                  onChange={e => setForm({...form, description: e.target.value})} 
                  rows={4}
                  className="form-input"
                  placeholder="Eğitimin içeriği, amacı ve hedef kitlesi hakkında kısa bilgi giriniz..." 
                />
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowModal(false)} className="btn-cancel">İptal Et</button>
              <button onClick={createCourse} className="btn-submit">Eğitimi Oluştur</button>
            </div>
          </div>
        </div>
      )}

      {/* Silme & Duyuru Onay Modalı */}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        requireText={confirmModal.requireText || false}
        requiredText="EVET"
        onConfirm={() => confirmModal.action && confirmModal.action()}
        onCancel={closeConfirm}
        confirmText={confirmModal.type === 'warning' ? 'Evet, Duyur' : 'Evet, Sil'}
      />

      <style>{`
        .page-container {
          animation: fadeSlideUp 0.4s ease-out;
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Sayfa Üst Kısmı */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          margin: 0 0 4px 0;
          font-size: 22px;
          font-weight: 700;
          color: #111827;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          margin: 0;
          font-size: 13.5px;
          color: #6b7280;
        }

        /* Ana Kart */
        .content-card {
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #eaecf0;
          box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03);
          overflow: hidden;
        }

        /* Arama Çubuğu */
        .search-bar-container {
          padding: 16px 24px;
          border-bottom: 1px solid #eaecf0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          background: #fcfcfd;
        }

        .search-input-wrapper {
          position: relative;
          width: 100%;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #98a2b3;
        }

        .search-input {
          width: 100%;
          padding: 10px 14px 10px 40px;
          border: 1px solid #d0d5dd;
          border-radius: 10px;
          font-size: 14px;
          outline: none;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
          transition: all 0.2s;
        }

        .search-input:focus {
          border-color: #e30613;
          box-shadow: 0 0 0 4px #fff1f2;
        }

        .result-count {
          font-size: 13px;
          color: #667085;
        }

        .result-count strong {
          color: #111827;
        }

        /* Tablo Stilleri */
        .table-responsive {
          overflow-x: auto;
        }

        .modern-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }

        .modern-table th {
          padding: 12px 24px;
          text-align: left;
          font-size: 11.5px;
          font-weight: 600;
          color: #667085;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: #f9fafb;
          border-bottom: 1px solid #eaecf0;
        }

        .modern-table td {
          padding: 16px 24px;
          border-bottom: 1px solid #eaecf0;
          vertical-align: middle;
          transition: background 0.2s;
        }

        .modern-table tr:last-child td {
          border-bottom: none;
        }

        .modern-table tr:hover td {
          background: #f9fafb;
        }

        .text-dark {
          color: #111827;
        }

        .course-icon-bg {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: #f3f4f6;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* Badgeler (Etiketler) */
        .badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .category-badge {
          background: #f3f4f6;
          color: #4b5563;
          border: 1px solid #e5e7eb;
        }

        .status-active {
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
        }
        
        .status-active::before {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #10b981;
          margin-right: 6px;
        }

        .status-passive {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        }

        .status-passive::before {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #9ca3af;
          margin-right: 6px;
        }

        /* Butonlar */
        .action-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary {
          background: #e30613;
          color: white;
          border: 1px solid #e30613;
          border-radius: 10px;
          padding: 10px 18px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13.5px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
          transition: all 0.2s;
        }

        .btn-primary:hover {
          background: #ba000a;
          border-color: #ba000a;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(227, 6, 19, 0.2);
        }

        .btn-outline {
          background: #ffffff;
          border: 1px solid #d0d5dd;
          color: #344054;
          border-radius: 8px;
          padding: 8px 14px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .btn-outline:hover {
          background: #f9fafb;
          border-color: #98a2b3;
          color: #111827;
        }

        .btn-announce {
          background: #ffffff;
          border: 1px solid #d0d5dd;
          color: #4b5563;
          border-radius: 8px;
          padding: 8px 14px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .btn-announce:hover:not(:disabled) {
          background: #fff1f2;
          border-color: #fca5a5;
          color: #e30613;
        }

        .btn-announce:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .btn-icon-danger {
          background: #fef2f2;
          color: #ef4444;
          border: 1px solid #fee2e2;
          border-radius: 8px;
          padding: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: all 0.2s;
        }

        .btn-icon-danger:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        .empty-state {
          text-align: center;
          padding: 40px !important;
          color: #6b7280;
          font-size: 14px;
        }

        /* Modal Stilleri */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(16, 24, 40, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          zIndex: 1000;
          padding: 20px;
        }

        .modal-container {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08);
          animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow: hidden;
        }

        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .modal-header {
          padding: 24px 24px 16px;
          border-bottom: 1px solid #eaecf0;
        }

        .modal-title {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .modal-subtitle {
          margin: 0;
          font-size: 13.5px;
          color: #6b7280;
        }

        .modal-body {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          font-size: 13px;
          color: #344054;
        }

        .form-input {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #d0d5dd;
          border-radius: 10px;
          font-size: 14px;
          outline: none;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
          transition: all 0.2s;
          resize: vertical;
        }

        .form-input:focus {
          border-color: #e30613;
          box-shadow: 0 0 0 4px #fff1f2;
        }

        .modal-footer {
          padding: 16px 24px;
          background: #f9fafb;
          border-top: 1px solid #eaecf0;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-cancel {
          padding: 10px 16px;
          border: 1px solid #d0d5dd;
          background: #ffffff;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          color: #344054;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f9fafb;
          border-color: #98a2b3;
        }

        .btn-submit {
          padding: 10px 18px;
          background: #e30613;
          color: #ffffff;
          border: 1px solid #e30613;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
          transition: all 0.2s;
        }

        .btn-submit:hover {
          background: #ba000a;
          border-color: #ba000a;
        }
      `}</style>
    </AdminLayout>
  );
}
